import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Autocomplete,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { handleError, fetchServicesForInput, removeNullsAndEmptys, validateNonEmpty } from "../../utils/lib";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "./EditPage";
import { useTranslation } from 'react-i18next';

const EditService = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);

  const {updateService} = location.state || {};

  const [service, setService] = useState(
    updateService || {
      name: "",
      categories: [],
      is_study: 0,
      duration: "",
      cost: "",
      tax: "",
      // Price 1
      format_p1: null,
      margin_p1: "",
      p1: "",
      // Price 2
      format_p2: null,
      margin_p2: "",
      p2: "",
      // Price 3
      format_p3: null,
      margin_p3: "",
      p3: "",
    }
  );

  const [categories, setCategories] = useState([]);
  
  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    name: useRef(null),
    categories: useRef(null),
    duration: useRef(null),
    cost: useRef(null),
    tax: useRef(null),
    // Price 1 refs
    format_p1: useRef(null),
    margin_p1: useRef(null),
    p1: useRef(null),
    // Price 2 refs
    format_p2: useRef(null),
    margin_p2: useRef(null),
    p2: useRef(null),
    // Price 3 refs
    format_p3: useRef(null),
    margin_p3: useRef(null),
    p3: useRef(null),
  });

  const [checked, setChecked] = useState(service.is_study === 1);

  useEffect(() => {
    updateTitle(
      updateService ? updateService.thumb_url : '',
      updateService ? t('edit.editService') : t('edit.newServiceTitle'),
      t('common.completeData')
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        const servicesForInput = await fetchServicesForInput();
        setCategories(servicesForInput.products_categories);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        // Add a delay to focus to ensure component is fully rendered
        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);
      }
    };
    fetchData();
   
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setService({ ...service, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateNonEmpty("name", service.name, setErrors, refs.name) ||
      !validateNonEmpty("categories", service.categories?.length ? service.categories[0].name : '', setErrors, refs.categories)
    ) {
      return;
    }

    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Map string display values to enum values for price format fields
      const serviceToSave = {
        ...service,
        format_p1: mapPriceFormatToEnum(service.format_p1),
        format_p2: mapPriceFormatToEnum(service.format_p2),
        format_p3: mapPriceFormatToEnum(service.format_p3)
      };
      
      //console.log(removeNullsAndEmptys(serviceToSave));
      let response;
      if (updateService) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}products`,
          removeNullsAndEmptys(serviceToSave),
          { params: { service: '', id: updateService.id }, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}products`,
          removeNullsAndEmptys(serviceToSave),
          { params: { service: '' }, withCredentials: true }
        );
      }

      navigate(`/main/view-product`, { state: { id: response.data.data.id } });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  // Map display string to enum value
  const mapPriceFormatToEnum = (displayFormat) => {
    if (!displayFormat) return null;
    
    if (displayFormat === t('common.fixed')) return "FIXED";
    if (displayFormat === t('common.costMargin')) return "COST_MARGIN";
    if (displayFormat === t('common.costMarginTax')) return "COST_MARGIN_TAX";
    
    return displayFormat;
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = [
        'name', 'categories', 'duration', 'cost', 'tax',
        // Price 1 fields
        'format_p1', 'margin_p1', 'fixed_p1',
        // Price 2 fields
        'format_p2', 'margin_p2', 'fixed_p2',
        // Price 3 fields
        'format_p3', 'margin_p3', 'fixed_p3'
      ];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };
  
  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading}
    >
      <TextField
        fullWidth
        margin="normal"
        label={t('common.nameStar')}
        name="name"
        value={service.name}
        onChange={handleChange}
        inputRef={refs.name}
        error={!!errors.name}
        helperText={errors.name}
        onKeyDown={(e) => handleKeyDown(e, 'name')}
      />

      <Autocomplete
        fullWidth
        multiple
        options={categories}
        getOptionLabel={(option) => (option ? option.name : "")} 
        value={service.categories || []}
        onChange={(e, newValue) =>
          setService({
            ...service,
            categories: newValue,
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('edit.categoriesStar')}
            name="categories"
            margin="normal"
            inputRef={refs.categories}
            error={!!errors.categories}
            helperText={errors.categories}
            onKeyDown={(e) => handleKeyDown(e, 'categories')}
          />
        )}
      />

      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setChecked(isChecked);
                setService({
                  ...service,
                  is_study: isChecked ? 1 : 0
                });
              }}
            />
          }
          label={t('edit.isStudy')}
        />
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('edit.durationMinutes')}
        name="duration"
        value={service.duration}
        onChange={handleChange}
        inputRef={refs.duration}
        error={!!errors.duration}
        helperText={errors.duration}
        onKeyDown={(e) => handleKeyDown(e, 'duration')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.cost')}
        name="cost"
        value={service.cost}
        onChange={handleChange}
        inputRef={refs.cost}
        error={!!errors.cost}
        helperText={errors.cost}
        onKeyDown={(e) => handleKeyDown(e, 'cost')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.tax')}
        name="tax"
        value={service.tax}
        onChange={handleChange}
        inputRef={refs.tax}
        error={!!errors.tax}
        helperText={errors.tax}
        onKeyDown={(e) => handleKeyDown(e, 'tax')}
      />

      {/* Price 1 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price1')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={service.format_p1}
            onChange={(e, newValue) => {
              setService(prev => ({
                ...prev,
                format_p1: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p1"
                inputRef={refs.format_p1}
                onKeyDown={(e) => handleKeyDown(e, 'format_p1')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p1"
            value={service.margin_p1}
            onChange={(e) => handleChange}
            inputRef={refs.margin_p1}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p1')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p1"
            value={service.fixed_p1}
            onChange={(e) => handleChange}
            inputRef={refs.fixed_p1}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p1')}
            disabled={service.format_p1 !== t('common.fixed')}
          />
        </Box>
      </Box>

      {/* Price 2 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price2')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={service.format_p2}
            onChange={(e, newValue) => {
              setService(prev => ({
                ...prev,
                format_p2: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p2"
                inputRef={refs.format_p2}
                onKeyDown={(e) => handleKeyDown(e, 'format_p2')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p2"
            value={service.margin_p2}
            onChange={(e) => handleChange}
            inputRef={refs.margin_p2}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p2')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p2"
            value={service.fixed_p2}
            onChange={(e) => handleChange}
            inputRef={refs.fixed_p2}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p2')}
            disabled={service.format_p2 !== t('common.fixed')}
          />
        </Box>
      </Box>

      {/* Price 3 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price3')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={service.format_p3}
            onChange={(e, newValue) => {
              setService(prev => ({
                ...prev,
                format_p3: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p3"
                inputRef={refs.format_p3}
                onKeyDown={(e) => handleKeyDown(e, 'format_p3')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p3"
            value={service.margin_p3}
            onChange={(e) => handleChange}
            inputRef={refs.margin_p3}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p3')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p3"
            value={service.fixed_p3}
            onChange={(e) => handleChange}
            inputRef={refs.fixed_p3}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p3')}
            disabled={service.format_p3 !== t('common.fixed')}
          />
        </Box>
      </Box>
    </EditPage>
  );
};

export default EditService;
